import { useEffect, useContext, createContext, ReactNode } from "react";
import { shadowRoot } from "./shadow";

interface ScrollLockProps {
  isDisabled?: boolean;
  scrollableSelector?: string;
  children?: ReactNode;
  appWindow?: Window;
}

const WindowContext = createContext<Window | null>(null);

// Enhanced utility to check if an element should be scrollable
const isElementScrollable = (element: Element): boolean => {
  const style = window.getComputedStyle(element);

  // Check for explicit overflow properties
  const hasOverflowScroll = /(auto|scroll)/.test(
    style.overflow + style.overflowY + style.overflowX
  );

  // Check for scrollable class or data attribute
  const hasScrollableMarker =
    element.classList.contains("scrollable") ||
    element.getAttribute("data-scrollable") === "true";

  // Check if element has overflow content
  const hasOverflowContent =
    element instanceof HTMLElement &&
    (element.scrollHeight > element.clientHeight ||
      element.scrollWidth > element.clientWidth);

  // Check for common scrollable patterns
  const hasScrollablePattern =
    (style.height === "100%" || style.maxHeight !== "none") &&
    (hasOverflowContent ||
      style.overflowY === "auto" ||
      style.overflowY === "scroll");

  // NOTE(liamvdv): helpful if you are wondering why your scroll is not working / captured by ScrollLock.
  // console.log(
  //   "hasOverflowScroll",
  //   hasOverflowScroll,
  //   "hasScrollableMarker",
  //   hasScrollableMarker,
  //   "hasScrollablePattern",
  //   hasScrollablePattern
  // );
  return hasOverflowScroll || hasScrollableMarker || hasScrollablePattern;
};

// Get the actual target element within shadow root
const getTargetInShadowRoot = (event: TouchEvent): Element | null => {
  const host = event.target as Element;
  if (!host.id?.includes("shadow-host")) return host;

  try {
    const root = shadowRoot();
    if (!root) return null;

    // Get touch coordinates
    const touch = event.touches[0];
    const { clientX, clientY } = touch;

    // Find the actual element at the coordinates within the shadow root
    return root.elementFromPoint(clientX, clientY);
  } catch (error) {
    console.error("Error accessing shadow root:", error);
    return null;
  }
};

// Find scrollable parent within shadow root context
const findScrollableParent = (
  element: Element | null,
  selector: string
): Element | null => {
  if (!element) return null;

  while (element) {
    // Check if the element is scrollable
    if (isElementScrollable(element)) {
      return element;
    }

    // Check for scrollable elements using the selector
    const closest = element.closest(selector);
    if (closest) {
      return closest;
    }

    // Move up to parent element
    element = element.parentElement;
  }
  return null;
};

const ScrollLock: React.FC<ScrollLockProps> = ({
  children,
  isDisabled = false,
  scrollableSelector = ".scrollable",
  appWindow = window,
}) => {
  const windowContext = useContext(WindowContext);
  const win = windowContext || appWindow;
  const doc = win.document;

  useEffect(() => {
    if (isDisabled) return;

    const touchStartY = { current: 0 };

    const preventScroll = (event: TouchEvent) => {
      const targetElement = getTargetInShadowRoot(event);
      if (!targetElement) {
        event.preventDefault();
        return;
      }

      const scrollableParent = findScrollableParent(
        targetElement,
        scrollableSelector
      );
      if (!scrollableParent) {
        event.preventDefault();
      }
    };

    const handleTouchStart = (event: TouchEvent) => {
      touchStartY.current = event.touches[0].clientY;
    };

    const handleTouchMove = (event: TouchEvent) => {
      const targetElement = getTargetInShadowRoot(event);
      if (!targetElement) {
        event.preventDefault();
        return;
      }

      const touchY = event.touches[0].clientY;
      const direction = touchStartY.current - touchY;

      const scrollableParent = findScrollableParent(
        targetElement,
        scrollableSelector
      );

      if (!scrollableParent) {
        return preventScroll(event);
      }

      const { scrollTop, scrollHeight, clientHeight } =
        scrollableParent as HTMLElement;
      // Prevent overscroll at boundaries while allowing normal scroll
      if (
        (scrollTop <= 0 && direction < 0) || // Attempting to scroll up at top
        (scrollTop + clientHeight >= scrollHeight && direction > 0) // Attempting to scroll down at bottom
      ) {
        event.preventDefault();
      }
    };

    // Apply overflow hidden to body
    const originalStyle = doc.body.style.overflow;
    doc.body.style.overflow = "hidden";

    doc.addEventListener("touchstart", handleTouchStart, { passive: false });
    doc.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      doc.body.style.overflow = originalStyle;
      doc.removeEventListener("touchstart", handleTouchStart);
      doc.removeEventListener("touchmove", handleTouchMove);
    };
  }, [isDisabled, doc, scrollableSelector]);

  return <>{children}</>;
};

export default ScrollLock;
